import { useCallback, useMemo } from 'react';
import { useStatefulRef } from '../../utils/hooks/useStatefulRef';
/**
 * A hook that provides a registry for managing timestamped items with unique IDs.
 *
 * The registry maintains a collection of items where:
 * - When adding items with the same ID, only the item with the most recent timestamp is kept
 * - getter methods hold a ref to the registry so they are updated when the registry is updated,
 *   and can be used to get the latest values in async operations
 */
export const useItemRegistry = () => {
  const [getQueue, setQueue] = useStatefulRef(new Map());
  const setItemIfNewer = useCallback((queue, item) => {
    const existingItem = queue.get(item.id);
    if (!existingItem || item.timestamp > existingItem.timestamp) {
      queue.set(item.id, item);
    }
  }, []);
  const add = useCallback((...items) => setQueue(queue => {
    const newQueue = new Map(queue);
    items.forEach(item => setItemIfNewer(newQueue, item));
    return newQueue;
  }), [setItemIfNewer, setQueue]);
  const filter = useCallback(predicate => {
    setQueue(queue => new Map([...queue].filter(([, item]) => predicate(item))));
  }, [setQueue]);
  const remove = useCallback(id => {
    setQueue(queue => {
      const newQueue = new Map(queue);
      newQueue.delete(id);
      return newQueue;
    });
  }, [setQueue]);
  const clear = useCallback(() => setQueue(new Map()), [setQueue]);
  const get = useCallback(id => getQueue().get(id), [getQueue]);
  const getAll = useCallback(() => Array.from(getQueue().values()), [getQueue]);
  const hasItem = useCallback(id => getQueue().has(id), [getQueue]);
  const size = useCallback(() => getQueue().size, [getQueue]);
  return useMemo(() => ({
    add,
    clear,
    filter,
    get,
    getAll,
    hasItem,
    remove,
    size
  }), [add, clear, filter, get, getAll, hasItem, remove, size]);
};